import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  IconButton,
  Avatar,
  TextField,
  Button
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LinkIcon from "@mui/icons-material/Link";
import axios from "axios";
import { BASE_URL } from "../../Api/ApiUrls";
import CustomBottomNavbar from "../../Users/Post Module/FormsBottomNavbar";
import { AuthContext } from "../../AuthContext/AuthContext";
import GuestLoginModal from '../../GuestLoginModal';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
const RESPONSE_API_ENDPOINT = `${BASE_URL}/response-property-request/`;

const WantedPropDetails = () => {
  const { req_id } = useParams();
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);
  
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  
  // Comments section state (for URL inputs)
  const [openComments, setOpenComments] = useState(true);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [propertyLink, setPropertyLink] = useState("");
  
  // Responses section state (for displaying data)
  const [openResponses, setOpenResponses] = useState(true);
  
  const [submitting, setSubmitting] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  const colors = {
    primary: "#1a1a1a",
    secondary: "#8B7355",
    accent: "#A52A2A",
    background: "#b9a990",
    cardBackground: "#FFFFFF",
    textDark: "#1a1a1a",
    textLight: "#FFFFFF",
    textMuted: "#666666"
  };

  // Fetch users for mapping
  useEffect(() => {
    fetch(`${BASE_URL}/users/`)
      .then(res => res.json())
      .then(data => {
        const map = {};
        data.forEach(user => {
          map[user.user_id] = user.username;
        });
        setUsersMap(map);
      })
      .catch(err => console.error("Failed to fetch users", err));
  }, []);

  const requireLogin = (e, callback) => {
    e.stopPropagation();
    if (isGuest) {
      setShowGuestModal(true);
      return;
    }
    callback();
  };

  const formatPropertyId = (id) => {
    return `WP${String(id).padStart(4, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const formatBudget = (min, max) => {
    if (!min && !max) return "Not specified";

    const formatNumber = (num) => {
      const numValue = parseFloat(num) || 0;

      if (numValue >= 10000000) {
        return `₹${(numValue / 10000000).toFixed(1)} Cr`;
      } else if (numValue >= 100000) {
        return `₹${(numValue / 100000).toFixed(1)} L`;
      } else if (numValue >= 1000) {
        return `₹${(numValue / 1000).toFixed(1)} K`;
      } else {
        return `₹${numValue}`;
      }
    };

    const minNum = parseFloat(min) || 0;
    const maxNum = parseFloat(max) || 0;

    if (minNum === maxNum) return formatNumber(minNum);

    const minFormatted = formatNumber(minNum);
    const maxFormatted = formatNumber(maxNum);

    const getSuffix = (str) => {
      if (str.includes('Cr')) return 'Cr';
      if (str.includes('L')) return 'L';
      if (str.includes('K')) return 'K';
      return '';
    };

    const minSuffix = getSuffix(minFormatted);
    const maxSuffix = getSuffix(maxFormatted);

    if (minSuffix && minSuffix === maxSuffix) {
      const minValue = minFormatted.replace(` ${minSuffix}`, '').replace('₹', '');
      const maxValue = maxFormatted.replace(` ${maxSuffix}`, '').replace('₹', '');
      return `₹${minValue}-${maxValue} ${minSuffix}`;
    }

    return `${minFormatted} - ${maxFormatted}`;
  };

  // Property link validation patterns
  const getValidLinkPatterns = () => {
    const lookingFor = data?.looking_for?.toLowerCase() || '';
    
    const basePatterns = {
      purchase: [
        /^https?:\/\/landnest\.net\/buy-description\/\d+$/,
        /^https?:\/\/localhost:3000\/buy-description\/\d+$/,
        /^https?:\/\/app\.propertypincoder\.com\/buy-description\/\d+$/,
        /^https?:\/\/app\.propertypincoder\.com\/hot-property-description\/\d+$/
      ],
      rent: [
        /^https?:\/\/landnest\.net\/rent-description\/\d+$/,
        /^https?:\/\/localhost:3000\/rent-description\/\d+$/,
        /^https?:\/\/app\.propertypincoder\.com\/rent-description\/\d+$/
      ],
      lease: [
        /^https?:\/\/landnest\.net\/lease-description\/\d+$/,
        /^https?:\/\/localhost:3000\/lease-description\/\d+$/,
        /^https?:\/\/app\.propertypincoder\.com\/lease-description\/\d+$/
      ]
    };

    if (lookingFor.includes('purchase')) return basePatterns.purchase;
    if (lookingFor.includes('rent')) return basePatterns.rent;
    if (lookingFor.includes('lease')) return basePatterns.lease;
    
    return [
      /^https?:\/\/landnest\.net\/.+\/\d+$/,
      /^https?:\/\/localhost:3000\/.+\/\d+$/,
      /^https?:\/\/app\.propertypincoder\.com\/.+\/\d+$/
    ];
  };

  const isValidPropertyLink = (link) => {
    const patterns = getValidLinkPatterns();
    return patterns.some(pattern => pattern.test(link));
  };

  // Handle submit for URL
  const handleSubmitUrl = async () => {
    if (!propertyLink.trim()) {
      setAlertMessage("Please enter a property link");
      setShowFailure(true);
      return;
    }

    if (!isValidPropertyLink(propertyLink.trim())) {
      const lookingFor = data?.looking_for?.toLowerCase() || '';
      let validExamples = "";
      
      if (lookingFor.includes('purchase')) {
        validExamples = "https://landnest.net/buy-description/162 or http://localhost:3000/buy-description/59";
      } else if (lookingFor.includes('rent')) {
        validExamples = "https://landnest.net/rent-description/162 or http://localhost:3000/rent-description/88";
      } else if (lookingFor.includes('lease')) {
        validExamples = "https://landnest.net/lease-description/162 or http://localhost:3000/lease-description/99";
      } else {
        validExamples = "Please use proper landnest.net links";
      }
      
      setAlertMessage(`Invalid property link.\n\nValid examples:\n${validExamples}`);
      setShowFailure(true);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(RESPONSE_API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          comment: "",
          proerty_link: propertyLink, 
          req_id: parseInt(req_id), 
          user_id: userId 
        }),
      });

      if (response.ok) {
        const newResponse = await response.json();
        setResponses(prev => [newResponse, ...prev]);
        setPropertyLink("");
        setShowCommentInput(false);
        setAlertMessage("Property link submitted successfully!");
        setShowSuccess(true);
      } else {
        throw new Error("Failed to submit");
      }
    } catch (err) {
      console.error("Error submitting:", err);
      setAlertMessage("Failed to submit. Please try again.");
      setShowFailure(true);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const [propertyRes, responsesRes] = await Promise.all([
          axios.get(`${BASE_URL}/property-request/${req_id}/`),
          axios.get(`${BASE_URL}/response-property-request/`)
        ]);
        
        setData(propertyRes.data);
        
        // Filter responses for this specific property request
        const propertyResponses = responsesRes.data
          .filter(response => response.req_id == req_id)
          .reverse();
        
        setResponses(propertyResponses);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [req_id]);

  const toggleComments = () => {
    setOpenComments(prev => !prev);
  };

  const toggleResponses = () => {
    setOpenResponses(prev => !prev);
  };

  const handleViewAllResponses = () => {
    navigate("/all-responses", {
      state: {
        responses: responses,
        property: data
      }
    });
  };

  // Filter responses that have links
  const responseItems = responses.filter(r => r.proerty_link);

  // Render response item for display
  const renderResponseItem = (response, index) => (
    <Box
      key={index}
      sx={{
        mb: 1,
        p: 1,
        backgroundColor: 'white',
        borderRadius: 0.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Avatar
          sx={{
            width: 20,
            height: 20,
            mr: 0.75,
            bgcolor: colors.secondary,
            fontSize: '0.6rem',
            color: colors.textLight
          }}
        >
          {usersMap[response.user_id]?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Typography
            variant="caption"
            fontWeight="bold"
            color={colors.primary}
            sx={{ fontSize: '0.65rem' }}
          >
            {usersMap[response.user_id] || `User ${response.user_id}`}
          </Typography>

          <Typography
            variant="caption"
            color={colors.textMuted}
            sx={{ fontSize: '0.6rem' }}
          >
            {formatDate(response.created_at || new Date())}
          </Typography>
        </Box>
      </Box>
      
      {/* Show property link */}
      {response.proerty_link && (
        <Box sx={{ mt: 0.5 }}>
          <Typography 
            component="a" 
            href={response.proerty_link} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              color: '#1976d2', 
              textDecoration: 'underline', 
              fontSize: '0.65rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { 
                color: '#1565c0', 
                textDecoration: 'underline' 
              } 
            }}
          >
            <LinkIcon sx={{ fontSize: '0.7rem' }} />
            View Property
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center",
        backgroundColor: 'rgb(239, 231, 221)'
      }}>
        <CircularProgress size={40} thickness={5} sx={{ color: '#000000' }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center",
        backgroundColor: colors.background
      }}>
        <Typography textAlign="center">No data found</Typography>
      </Box>
    );
  }

return (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#d8ccba" }}>
    {/* Fixed Header */}
    <Box
      sx={{
        backgroundColor: "#000",
        color: "#fff",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
      }}
    >
      <IconButton sx={{ color: "#fff" }} onClick={() => navigate(-1)}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      <Typography fontWeight={600} flexGrow={1}>
        Property Request Details
      </Typography>

      <Box textAlign="right">
        <Typography variant="caption">
          ID: {formatPropertyId(data.req_id)}
        </Typography>
        <Typography variant="caption" display="block">
          {new Date(data.created_at).toLocaleDateString()}
        </Typography>
      </Box>
    </Box>

    {/* Scrollable Content */}
    <Box sx={{ 
      flex: 1, 
      overflowY: "auto",
      mt: "56px", // Height of fixed header
      mb: "56px", // Height of bottom navbar
      px: 2,
      py: 2
    }}>
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2,
            p: 1,
            backgroundColor: colors.primary,
            borderRadius: 1,
            color: colors.textLight
          }}>
            <Box sx={{ 
              backgroundColor: "#b9a990", 
              color: colors.primary,
              px: 1.5,
              py: 0.5,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" fontWeight="bold">
                {data.looking_for ? data.looking_for.charAt(0).toUpperCase() + data.looking_for.slice(1) : "Property"}
              </Typography>
            </Box>
            
            <Box sx={{ 
              backgroundColor: colors.cardBackground, 
              color: colors.primary,
              px: 1.5,
              py: 0.5,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" fontWeight="bold">
                Budget: {formatBudget(data.min_budget, data.max_budget)}
              </Typography>
            </Box>
          </Box>

          <Detail label="Property Type" value={data.property_type || "Not specified"} />
          <Divider sx={{ my: 1 }} />

          {/* Budget Section */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: colors.accent }}>
            Budget Details
          </Typography>
          
          {data.looking_for === "rent" ? (
            <>
              <Detail label="Monthly Rent Range" value={`₹${data.min_monthly_rent} - ₹${data.max_monthly_rent}`} />
              <Detail label="Lease Period" value={`${data.min_year_lease} - ${data.max_year_lease} years`} />
            </>
          ) : (
            <>
              <Detail label="Budget Range" value={`₹${data.min_budget} - ₹${data.max_budget}`} />
              
              {(data.min_budget_per_acre || data.max_budget_per_acre) && (
                <Detail 
                  label="Budget Per Acre" 
                  value={`₹${data.min_budget_per_acre || 0} - ₹${data.max_budget_per_acre || 0}`} 
                />
              )}
            </>
          )}

          {(data.min_expected_rental_income || data.max_expected_rental_income) && (
            <Detail 
              label="Expected Rental Income" 
              value={`₹${data.min_expected_rental_income || 0} - ₹${data.max_expected_rental_income || 0}`} 
            />
          )}

          <Divider sx={{ my: 1 }} />

          {/* Property Specifications */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: colors.accent }}>
            Property Specifications
          </Typography>

          {(data.length > 0 && data.width > 0) && (
            <Detail label="Dimensions" value={`${data.length} x ${data.width} ${data.units}`} />
          )}

          {data.area > 0 && (
            <Detail label="Area" value={`${data.area} ${data.units}²`} />
          )}

          {(data.min_acres > 0 || data.max_acres > 0) && (
            <Detail
              label="Land Size (Acres)"
              value={`${data.min_acres}${data.max_acres > data.min_acres ? ` - ${data.max_acres}` : ""} acres`}
            />
          )}

          {data.ratio && <Detail label="Ratio" value={data.ratio} />}
          {data.floor > 0 && <Detail label="Floor" value={data.floor} />}
          {data.no_of_bedrooms > 0 && <Detail label="Bedrooms" value={data.no_of_bedrooms} />}

          <Divider sx={{ my: 1 }} />

          {/* Locations */}
          {data.pro_loc && data.pro_loc.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: colors.accent }}>
                Preferred Locations
              </Typography>
              {data.pro_loc.map((loc, idx) => (
                <Typography key={idx} sx={{ fontSize: "0.85rem", mt: 0.5, color: colors.textMuted }}>
                  📍 {loc.location}
                </Typography>
              ))}
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* User and Timestamp Info */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: colors.accent }}>
            Request Information
          </Typography>
          
          <Detail label="Last Updated" value={new Date(data.updated_at).toLocaleString()} />

     {data.comment && (
  <>
    <Divider sx={{ my: 1 }} />

    {/* Header */}
<Typography
  variant="subtitle1"
  fontWeight={700}
  sx={{ color: colors.accent, mb: 0.5 }} 
>
  Comments
</Typography>

{/* Comment text - without p tag */}
<Typography
  sx={{
    fontSize: "0.85rem",
    color: colors.textMuted,
    lineHeight: 1.2 
  }}
>
  Please paste url if you have related property in app
</Typography>

    {/* URL INPUT FIELD - Always Visible */}
    <Box
      sx={{
        mb: 2,
        mt: 2,
        p: 1.5,
        backgroundColor: "#f5f2ee",
        borderRadius: 1
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Paste property link"
        value={propertyLink}
        onChange={(e) => setPropertyLink(e.target.value)}
        size="small"
        sx={{ mb: 1 }}
        InputProps={{
          sx: {
            fontSize: "0.75rem",
            backgroundColor: "white"
          }
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
          {/* <Button
          size="small"
          onClick={() => setPropertyLink("")}
          sx={{
            color: colors.secondary,
            fontSize: "0.65rem",
            minWidth: "auto",
            px: 1
          }}
        >
          Cancel
        </Button> */}
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmitUrl}
          disabled={submitting || !propertyLink.trim()}
          sx={{
            fontSize: "0.65rem",
            minWidth: "auto",
            px: 1,
            backgroundColor: propertyLink.trim()
              ? colors.accent
              : "#ccc",
            color: colors.textLight,
            "&:hover": {
              backgroundColor: colors.accent
            }
          }}
        >
          {submitting ? "..." : "Submit"}
        </Button>
      </Box>
    </Box>
  </>
)}

          {/* ================= RESPONSES SECTION - DATA DISPLAY ================= */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #ccc' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: colors.accent }}
                >
                  Property Responses ({responseItems.length})
                </Typography>
              </Box>
              
              <Typography
                onClick={toggleResponses}
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: '#a89478ff'
                }}
              >
                {openResponses ? 'Hide' : 'Show'}
              </Typography>
            </Box>

            {/* RESPONSES DATA DISPLAY */}
            {openResponses && (
              <Box>
                {responseItems.length === 0 ? (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textMuted, textAlign: "center", py: 2 }}>
                    No property responses yet.
                  </Typography>
                ) : (
                  <>
                    {/* Show first 2 responses */}
                    {responseItems.slice(0, 2).map((response, index) => (
                      renderResponseItem(response, index)
                    ))}

                    {/* View All Link if more than 2 responses */}
                    {responseItems.length > 2 && (
                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Typography
                          onClick={handleViewAllResponses}
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: colors.secondary,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          View all {responseItems.length} responses →
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>

    {/* Guest Login Modal */}
    <GuestLoginModal
      open={showGuestModal}
      onClose={() => setShowGuestModal(false)}
    />

    {/* Success Alert */}
    {showSuccess && (
      <SuccessAlert
        message={alertMessage}
        onClose={() => {
          setShowSuccess(false);
          setAlertMessage("");
        }}
      />
    )}
    
    {/* Failure Alert */}
    {showFailure && (
      <FailureAlert
        message={alertMessage}
        onClose={() => {
          console.log("Closing failure alert");
          setShowFailure(false);
          setAlertMessage(""); 
        }}
      />
    )}

    {/* Fixed Bottom Navigation */}
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
      }}
    >
      <CustomBottomNavbar />
    </Box>
  </Box>
);
};

/* Reusable Row */
const Detail = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", my: 0.8 }}>
    <Typography fontWeight={600} fontSize="0.85rem">
      {label}
    </Typography>
    <Typography fontSize="0.85rem" color="text.secondary">
      {value || "—"}
    </Typography>
  </Box>
);

export default WantedPropDetails;